import type { BuildSummary, UserDTO } from '@va/shared';

declare const __VA_TARGET__: 'web' | 'desktop';

/** Result of a call proxied through the desktop app's main process. */
export interface BridgeResponse<T = unknown> {
  ok: boolean;
  status: number;
  data: T;
}

export interface LicenseStatus {
  signedIn: boolean;
  email: string | null;
  machineId: string | null;
  deviceId: string | null;
  licensePath: string;
  /** Bundle expiry (offline limit). */
  expiresAt: string | null;
  lastSyncAt: string | null;
  /** Product license codes currently unlocked on this machine. */
  codes: { code: string; source: string; expiresAt: string | null }[];
  error: { code: string; message: string; details?: unknown } | null;
  syncing: boolean;
}

export interface InstalledItem {
  productId: string;
  productName: string;
  buildId: string;
  version: string;
  platform: string;
  installType: string;
  targetDir: string;
  /** Plugin + app products: where the app went (Downloads/<Product>), beside the plugin's targetDir. */
  appDir?: string;
  files: string[];
  installedAt: string;
}

export interface InstallTarget {
  kind: 'ffgl' | 'wire' | 'app';
  label: string;
  path: string;
  detected: boolean;
  /** Can't be changed (apps always go to Downloads). */
  fixed: boolean;
  candidates: { label: string; path: string; exists: boolean }[];
}

export interface InstallProgress {
  productId: string;
  phase: 'download' | 'verify' | 'extract' | 'done' | 'error';
  received: number;
  total: number;
  message?: string;
}

/** The desktop app's own updates (apps/desktop/src/main/updater.ts). */
export interface AppUpdateState {
  /** available: a newer version to download by hand (an unsigned Mac copy can't update itself). */
  status: 'unsupported' | 'idle' | 'checking' | 'current' | 'available' | 'downloading' | 'ready' | 'error';
  version?: string;
  percent?: number;
  checkedAt?: string;
  error?: string;
}

/** API exposed by the Electron preload script as window.vaDesktop. */
export interface DesktopBridge {
  version: string;
  platform: 'win32' | 'darwin' | string;
  apiBase: string;
  request<T = unknown>(method: string, path: string, body?: unknown): Promise<BridgeResponse<T>>;
  auth: {
    session(): Promise<UserDTO | null>;
    login(email: string, password: string): Promise<BridgeResponse>;
    loginMfa(ticket: string, code: string): Promise<BridgeResponse>;
    register(input: { email: string; password: string; displayName?: string; acceptTerms: boolean }): Promise<BridgeResponse>;
    logout(): Promise<void>;
    onChange(cb: (user: UserDTO | null) => void): () => void;
  };
  license: {
    status(): Promise<LicenseStatus>;
    sync(): Promise<LicenseStatus>;
    onChange(cb: (s: LicenseStatus) => void): () => void;
  };
  installs: {
    list(): Promise<InstalledItem[]>;
    install(input: { productId: string; productName: string; build: BuildSummary }): Promise<InstalledItem>;
    uninstall(productId: string): Promise<void>;
    reveal(productId: string, part?: 'main' | 'app'): Promise<void>;
    targets(): Promise<InstallTarget[]>;
    chooseTarget(kind: InstallTarget['kind']): Promise<InstallTarget[]>;
    resetTarget(kind: InstallTarget['kind']): Promise<InstallTarget[]>;
    onProgress(cb: (p: InstallProgress) => void): () => void;
  };
  /** Paying in a window of the app (Stripe or PayPal's own page); the browser is the fallback. */
  checkout: {
    open(url: string, orderId: string): Promise<void>;
    reopen(): Promise<void>;
    inBrowser(): Promise<void>;
    onEvent(cb: (e: { orderId: string; outcome: 'complete' | 'cancel' | 'closed' | 'browser' }) => void): () => void;
  };
  updates: {
    status(): Promise<AppUpdateState>;
    check(): Promise<AppUpdateState>;
    /** Restarts the app into the downloaded version. */
    install(): Promise<void>;
    onChange(cb: (s: AppUpdateState) => void): () => void;
  };
  system: {
    openExternal(url: string): Promise<void>;
    diagnostics(): Promise<string>;
    hostsRunning(): Promise<string[]>;
    getSetting<T = unknown>(key: string): Promise<T | null>;
    setSetting(key: string, value: unknown): Promise<void>;
  };
}

export const target: 'web' | 'desktop' = typeof __VA_TARGET__ !== 'undefined' ? __VA_TARGET__ : 'web';

export const desktop: DesktopBridge | null = (window as unknown as { vaDesktop?: DesktopBridge }).vaDesktop ?? null;
export const isDesktop = !!desktop;

/** Server-relative media paths (/media/...) need the API origin inside the desktop app. */
export function mediaUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  if (desktop && path.startsWith('/')) return `${desktop.apiBase}${path}`;
  return path;
}

/** A newer build, or the same one now installed differently (its "Install as" was changed). */
export function needsUpdate(installed: { version: string; installType: string }, build: { version: string; installType: string }): boolean {
  return installed.version !== build.version || installed.installType !== build.installType;
}

export function openExternal(url: string): void {
  if (desktop) void desktop.system.openExternal(url);
  else window.open(url, '_blank', 'noopener,noreferrer');
}

/** Absolute URL of a page on the web store (used for links the desktop app opens in a browser). */
export function webStoreUrl(path: string): string {
  if (desktop) return `${desktop.apiBase}${path}`;
  return `${window.location.origin}${path}`;
}
